import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { Autocomplete, Stack, TextField } from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import { useCreatePledge, useUpdatePledge } from "features/finances";
import { FILTER_OPERATIONS, MEMBER_STATES, useMembers } from "features/members";
import { intersectionBy } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import { EthiopianDateField } from "../EthiopianDateField";
import { ValidationError } from "../ValidationError";
import { BaseModal } from "./BaseModal";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  flex: 1;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
  flex: 1;
  background-color: transparent;
`;

const CheckboxLabel = styled.label`
  font-size: 12px;
  margin-right: 16px;
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
`;

const StyledAutocomplete = styled(Autocomplete)`
  .MuiInputBase-root {
    font-size: 12px;
  }

  .MuiOutlinedInput-input {
    padding: 8px !important;
  }
`;

const pledgeSchema = yup.object({
  full_name: yup.string().when("isMember", {
    is: false,
    then: yup.string().required("Full name is required")
  }),
  phone_number: yup.string().when("isMember", {
    is: false,
    then: yup.string().required("Phone number is required")
  }),
  pledge_amount: yup
    .number()
    .typeError("Pledge amount must be a number")
    .required("Pledge amount is required"),
  pledge_frequency: yup.string().required("Pledge frequency is required"),
  interval: yup.number().when("pledge_frequency", {
    is: val => val === "MONTHLY" || val === "QUARTERLY" || val === "ANNUAL",
    then: yup
      .number()
      .typeError("Interval must be a number")
      .required("Interval is required")
      .min(1, "Interval must be at least 1"),
    otherwise: yup.number().typeError("Interval must be a number").nullable()
  }),
  start_date: yup.string().required("Start date is required"),
  end_date: yup.string().required("End date is required"),
  note: yup.string().notRequired()
});

export function PledgeModal({ isOpen, onClose, data }) {
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const membersQuery = useMembers({
    churchId: selectedChurch?.id,
    queries: {
      filter: {
        status: [
          {
            operator: FILTER_OPERATIONS.EQUAL,
            value: MEMBER_STATES.ACTIVE
          }
        ]
      }
    }
  });

  const createPledgeMutation = useCreatePledge();
  const updatePledgeMutation = useUpdatePledge();

  const [isMember, setIsMember] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [endDateDisabled, setEndDateDisabled] = useState(false);

  const { id } = useParams();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
    control
  } = useForm({
    defaultValues: {
      full_name: "",
      phone_number: "",
      pledge_amount: 0,
      paid_amount: 0,
      pledge_frequency: "",
      interval: 1,
      start_date: "",
      end_date: "",
      note: ""
    },
    resolver: yupResolver(pledgeSchema),
    context: { isMember }
  });

  const pledgeFrequency = watch("pledge_frequency");
  const startDate = watch("start_date");
  const endDate = watch("end_date");

  useEffect(() => {
    if (data && membersQuery.data) {
      setIsMember(data.full_name === null);

      const formattedStartDate = moment(data.start_date).format("YYYY-MM-DD");
      const formattedEndDate = moment(data.end_date).format("YYYY-MM-DD");

      if (data.pledge_frequency === "ONE_TIME") {
        setEndDateDisabled(true);
      } else {
        setEndDateDisabled(false);
      }

      setSelectedMembers(
        intersectionBy(membersQuery.data?.result, data.members, "id") || []
      );

      reset({
        ...data,
        full_name: data.full_name || "",
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        phone_number: data.full_name !== null ? data.phone_number : "",
        note: data.note || ""
      });
    }
  }, [data, reset, membersQuery.data]);

  const onSubmit = values => {
    const pledgeData = {
      pledge_amount: values.pledge_amount || 0,
      paid_amount: values.paid_amount || 0,
      pledge_frequency: values.pledge_frequency || "",
      interval: values.interval || 1,
      start_date: values.start_date || "",
      end_date: values.end_date || "",
      note: values.note || "",
      phone_number: values.phone_number || "",
      church_id: selectedChurch?.id,
      member_ids: selectedMembers.map(member => member.id),
      full_name: !isMember ? values.full_name : ""
    };

    if (data) {
      updatePledgeMutation.mutate(
        {
          churchId: selectedChurch?.id,
          campaignId: id,
          pledgeId: data.id,
          pledgeData
        },
        {
          onSuccess: () => {
            reset();
            setSelectedMembers([]);
            setEndDateDisabled(false);
            onClose();
          }
        }
      );
    } else {
      createPledgeMutation.mutate(
        {
          churchId: selectedChurch?.id,
          campaignId: id,
          pledgeData
        },
        {
          onSuccess: () => {
            reset();
            setSelectedMembers([]);
            setEndDateDisabled(false);
            onClose();
          }
        }
      );
    }
  };

  const resetStates = () => {
    setSelectedMembers([]);
    setEndDateDisabled(false);
  };

  return (
    <BaseModal
      title={data ? "Edit Pledge" : "Create Pledge"}
      isOpen={isOpen}
      onClose={() => {
        resetStates();
        reset();
        onClose();
      }}
      footer={
        <Stack direction="row" spacing={2}>
          <Button
            flavor="outlined"
            size="small"
            style={{
              flex: 1,
              color: "#000",
              borderColor: "#000",
              borderWidth: 1
            }}
            onClick={() => {
              onClose();
              resetStates();
            }}
            disabled={
              createPledgeMutation.isLoading || updatePledgeMutation.isLoading
            }
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleSubmit(onSubmit)}
            style={{ flex: 1 }}
            disabled={
              createPledgeMutation.isLoading || updatePledgeMutation.isLoading
            }
          >
            Save
          </Button>
        </Stack>
      }
    >
      <Field>
        <Label htmlFor="full_name">Full Name</Label>
        <Stack direction="row" alignItems="center">
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={isMember}
              onChange={e => {
                setIsMember(e.target.checked);
                setValue("full_name", "");
                setSelectedMembers([]);
              }}
            />
            Is Member
          </CheckboxLabel>
          {isMember ? (
            <Stack sx={{ width: "100%" }} spacing={1}>
              <StyledAutocomplete
                multiple
                options={membersQuery.data?.result ?? []}
                getOptionLabel={member =>
                  `${member.first_name} ${member.father_name} ${member.last_name}`
                }
                value={selectedMembers}
                onChange={(_, newValue) => {
                  setSelectedMembers(newValue);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search members..."
                  />
                )}
                slotProps={{
                  paper: {
                    sx: {
                      "& .MuiAutocomplete-listbox": {
                        "& .MuiAutocomplete-option": {
                          fontSize: "12px"
                        }
                      }
                    }
                  }
                }}
                size="small"
              />
            </Stack>
          ) : (
            <Input id="full_name" {...register("full_name")} />
          )}
        </Stack>
        <ErrorMessage
          errors={errors}
          name="full_name"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      {!isMember && (
        <Field>
          <Label htmlFor="phone_number">Phone Number</Label>
          <Input id="phone_number" {...register("phone_number")} />
          <ErrorMessage
            errors={errors}
            name="phone_number"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>
      )}
      <Field>
        <Label htmlFor="pledge_amount">Total Amount</Label>
        <Controller
          name="pledge_amount"
          control={control}
          render={({ field }) => (
            <NumericFormat
              customInput={Input}
              id="pledge_amount"
              thousandSeparator
              allowNegative={false}
              onValueChange={values => {
                field.onChange(values.floatValue);
              }}
              value={field.value}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name="pledge_amount"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="pledge_frequency">Frequency</Label>
        <Select
          id="pledge_frequency"
          {...register("pledge_frequency")}
          onChange={e => {
            setValue("pledge_frequency", e.target.value);
            setEndDateDisabled(e.target.value === "ONE_TIME");
          }}
        >
          <option value="" disabled>
            Select Frequency
          </option>
          <option value="ONE_TIME">One Time</option>
          <option value="MONTHLY">Monthly</option>
          <option value="QUARTERLY">Quarterly</option>
          <option value="ANNUAL">Annually</option>
        </Select>
        <ErrorMessage
          errors={errors}
          name="pledge_frequency"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="interval">Number of frequency</Label>
        <Input
          id="interval"
          type="number"
          min={1}
          {...register("interval")}
          disabled={pledgeFrequency === "ONE_TIME"}
        />
        <ErrorMessage
          errors={errors}
          name="interval"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="start_date">Start date</Label>
        <EthiopianDateField
          size="small"
          value={startDate}
          onChange={e => {
            const newStartDate = e.target.value;
            setValue("start_date", newStartDate);

            if (pledgeFrequency === "ONE_TIME") {
              setValue("end_date", newStartDate);
            }
          }}
        />
        <ErrorMessage
          errors={errors}
          name="start_date"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="end_date">End date</Label>
        <EthiopianDateField
          size="small"
          value={endDate}
          disabled={endDateDisabled}
          onChange={e => {
            const newEndDate = e.target.value;
            setValue("end_date", newEndDate);
          }}
        />
        <ErrorMessage
          errors={errors}
          name="end_date"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="note">Note</Label>
        <Input id="note" {...register("note")} />
        <ErrorMessage
          errors={errors}
          name="note"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>

      <MutationError
        error={createPledgeMutation.error || updatePledgeMutation.error}
        sx={{ mt: 2 }}
      />
    </BaseModal>
  );
}
